export type ManifestEntry =
  | { src: string; type: "same-origin"; hash?: string; volatile?: boolean }
  | { src: string; type: "versioned-cdn"; hash?: string; volatile?: boolean }
  | {
      src: string;
      type: "dynamic-loader";
      host?: string;
      path?: string;
      query?: string;
      volatile?: boolean;
    }
  | { inline: string; type: "inline-stable"; hash: string; volatile?: false }
  | { inline: string; type: "inline-volatile"; volatile: true };

export type RuntimeManifest = {
  sameOriginHashes: Map<string, string>;
  inlineHashes: Set<string>;
  versionedPins: Set<string>;
  dynamicPins: Array<{ host?: string; path?: string; query?: string }>;
  filenameIndex: Map<string, string>;
};

export function coerceEntries(raw: any): ManifestEntry[] {
  if (Array.isArray(raw?.scripts)) return raw.scripts as ManifestEntry[];
  if (raw && raw.scripts && typeof raw.scripts === "object") {
    const out: ManifestEntry[] = [];
    for (const [k, v] of Object.entries(raw.scripts)) {
      if (typeof v === "string") {
        out.push({ src: k, type: "same-origin", hash: v as string });
      }
    }
    return out;
  }
  return [];
}

export function buildRuntimeManifest(entries: ManifestEntry[]): RuntimeManifest {
  const sameOriginHashes = new Map<string, string>();
  const inlineHashes = new Set<string>();
  const versionedPins = new Set<string>();
  const dynamicPins: Array<{ host?: string; path?: string; query?: string }> = [];
  const filenameIndex = new Map<string, string>();

  for (const e of entries) {
    if ("inline" in e) {
      if (e.type === "inline-stable" && e.hash) inlineHashes.add(e.hash);
      continue;
    }
    if (e.type === "same-origin" && e.hash) {
      try {
        const u = new URL(e.src, window.location.href);
        sameOriginHashes.set(u.pathname, e.hash);
        sameOriginHashes.set(decodeURIComponent(u.pathname), e.hash);
        sameOriginHashes.set(e.src, e.hash);
        const fn = (u.pathname.split("/").pop() || "").toLowerCase();
        if (fn) filenameIndex.set(fn, e.hash);
      } catch {
        sameOriginHashes.set(e.src, e.hash);
        const fn = (e.src.split("/").pop() || "").toLowerCase();
        if (fn) filenameIndex.set(fn, e.hash);
      }
    } else if (e.type === "versioned-cdn") {
      versionedPins.add(e.src);
    } else if (e.type === "dynamic-loader") {
      dynamicPins.push({ host: e.host, path: e.path, query: e.query });
    }
  }
  return {
    sameOriginHashes,
    inlineHashes,
    versionedPins,
    dynamicPins,
    filenameIndex,
  };
}

export function urlMatchesPin(src: string, pins: RuntimeManifest["dynamicPins"]) {
  try {
    const u = new URL(src, window.location.href);
    return pins.some((p) => {
      if (p.host && u.host !== p.host) return false;
      if (p.path && u.pathname !== p.path) return false;
      if (p.query && u.search.replace(/^\?/, "") !== (p.query || "")) return false;
      return true;
    });
  } catch {
    return false;
  }
}
